import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import {TooltipOptions} from 'highcharts';
import {CHARTS_TRANSLATOR} from 'shared/EnumsConstants/card-charts-translate-keys.enum'; // NOSONAR

@Injectable()
export class ChartTemplateService {
  chartname: string;
  json: any;
  chart = {
    type: 'bar',
    marginRight: 20
  };

  constructor(private readonly translateService: TranslateService) {
    this.json = {};
  }

  setTitle(title: Title): void {
    this.json.title = this.getTitle(title);
  }

  setTooltip(tooltip: TooltipOptions): void {
    this.json.tooltip = tooltip;
  }

  setLegend(legend: Legend) {
    this.json.legend = this.getLegend(legend.reversed);
  }

  setxAxis(xAxis: Xaxis): void {
    if (xAxis.categories) {
      this.json.xAxis = this.getxAxis(xAxis.categories);
    } else {
      this.json.xAxis = xAxis;
    }
  }

  setyAxis(yAxis: Yaxis): void {
    if (yAxis.title) {
      this.json.yAxis = yAxis;
    } else {
      this.json.yAxis = this.getyAxis(
        yAxis.min,
        yAxis.max,
        yAxis.titleText,
        yAxis.tickInterval,
        yAxis.labelsFormat
      );
    }
  }

  setPlotOptions(stacking: string): void {
    this.json.plotOptions = this.getPlotOptions(stacking);
  }

  initializeDefaultValues() {
    this.json.credits = this.getCredit();
    this.json.exporting = this.getExportMenus();
    this.json.lang = this.getHighchartsTranslate();
  }

  initializeChartValues(
    series: any,
    title: Title,
    xAxis: Xaxis,
    yAxis: Yaxis,
    legend: Legend,
    stacking: string,
    tooltip: TooltipOptions,
    chartType?: ChartType
  ) {
    this.json.chart = this.chart;
    this.json.series = series;
    this.json.title = this.getTitle(title);
    this.setxAxis(xAxis);
    this.setyAxis(yAxis);
    this.setLegend(legend);
    if (chartType) {
      this.json.chart = chartType;
    } else {
      this.json.plotOptions = this.getPlotOptions(stacking);
      this.json.responsive = this.getResponsive();
    }
    if (tooltip) {
      this.setTooltip(tooltip);
    }

    this.json.credits = this.getCredit();
    this.json.exporting = this.getExportMenus();
    this.json.lang = this.getHighchartsTranslate();
    this.translateChartLabels();
  }

  translateChartLabels() {
    const preservedFunctions = new Map<string, unknown>();
    let template = this.jsonStringfyPreserveFunction(
      this.json,
      preservedFunctions
    );
    template = this.getTranslatedValues(template);
    const result = this.jsonParseRestoreFunction(template, preservedFunctions);
    this.json = result;
  }

  translateChart(value: string) {
    let result = value;
    result = this.getTranslatedValues(result);
    return result;
  }

  getTitle(title: Title): Title {
    return title;
  }

  getResponsive(): any {
    return {
      rules: [
        {
          condition: {
            maxWidth: 500
          },
          chartOptions: {
            plotOptions: {
              column: {
                pointWidth: 20,
                stacking: 'normal'
              }
            }
          }
        }
      ]
    };
  }

  getCredit(): any {
    return {
      enabled: false
    };
  }

  getExportMenus(): any {
    return {
      buttons: {
        contextButton: {
          enabled: true,
          symbol: 'url(/assets/images/download.svg)',
          verticalAlign: 'top',
          className: 'download-button',
          allowHTML: true,
          menuItems: [
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG'
          ]
        }
      }
    };
  }

  getHighchartsTranslate(): any {
    return {
      contextButtonTitle: this.translateService.instant(
        'i18n.cockpit.card.chart.contextMenuTitle'
      ),
      decimalPoint: '.',
      // viewFullscreen: this.translateService.instant('i18n.cockpit.card.chart.fullscreen'),
      printChart: this.translateService.instant(
        'i18n.cockpit.card.chart.itemPrint'
      ),
      downloadCSV: 'Download CSV',
      downloadJPEG: this.translateService.instant(
        'i18n.cockpit.card.chart.downloadJPEG'
      ),
      downloadPDF: this.translateService.instant(
        'i18n.cockpit.card.chart.itemDownloadPdf'
      ),
      downloadPNG: this.translateService.instant(
        'i18n.cockpit.card.chart.downloadPNG'
      ),
      downloadSVG: this.translateService.instant(
        'i18n.cockpit.card.chart.itemDownloadSvg'
      ),
      downloadXLS: 'Download XLS',
      // drillUpText: 'Back to {series.name}'
      exitFullscreen: 'Exit from full screen'
    };
  }

  private getTranslatedValues(result: string) {
    if (CHARTS_TRANSLATOR[this.chartname]) {
      CHARTS_TRANSLATOR[this.chartname].forEach(key => {
        const replacemntValue = this.translateService.instant(key);
        result = result.replace(new RegExp(key, 'g'), replacemntValue);
      });
    }
    return result;
  }

  private getLegend(isReversed: boolean) {
    return {
      reversed: isReversed
    };
  }

  private getxAxis(categories: string[]): any {
    return {
      categories
    };
  }

  private getyAxis(
    min: number,
    max?: number,
    axisTitelText?: string,
    tickInterval?: number,
    format?: string
  ): any {
    if (!max || max === 0) {
      return {
        min,
        title: {text: axisTitelText},
        labels: {format},
        tickInterval
      };
    }
    return {
      min,
      max,
      title: {text: axisTitelText},
      labels: {format},
      tickInterval
    };
  }

  private getPlotOptions(stacking: string): any {
    return {
      series: {
        stacking
      }
    };
  }

  private jsonStringfyPreserveFunction(
    json: unknown,
    preservedFunctions: Map<string, unknown>
  ): string {
    let functionIndex = 0;
    const result = JSON.stringify(json, (key, value) => {
      if (typeof value === 'function') {
        const functionToken = `__TEMOSTA_FN__${functionIndex++}`;
        preservedFunctions.set(functionToken, value);
        return functionToken;
      }
      return value;
    });
    return result;
  }

  private jsonParseRestoreFunction(
    json: string,
    preservedFunctions: Map<string, unknown>
  ): any {
    return JSON.parse(json, (key, value) => {
      if (
        typeof value === 'string' &&
        value.startsWith('__TEMOSTA_FN__') &&
        preservedFunctions.has(value)
      ) {
        return preservedFunctions.get(value);
      }
      return value;
    });
  }
}

export interface Yaxis {
  min?: number;
  max?: number;
  labelsFormat?: string;
  titleText?: string;
  tickInterval?: number;
  title?: string;
}

export interface Xaxis {
  categories: string[];
}

export interface Legend {
  reversed: boolean;
}

export interface Legend {
  reversed: boolean;
}

export interface ChartTooltip {
  pointFormat: unknown;
  valueDecimals?: number;
}

export interface Title {
  text: string;
  style: Highcharts.CSSObject;
}

export interface ChartType {
  type: string;
}
